import { useState } from "react";
import type { Bootstrap } from "../../shared/types";
import { api, type AccountInput } from "../lib/api";
import { formatMoney, parseMoney, todayISO } from "../lib/format";
import Modal from "./Modal";

// Per-person balance entry (Patrick's account + Amber's → combined), matching
// the design's "Current balance" dialog.
export default function BalanceModal({
  data,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Boxes start BLANK on purpose: a save only ever touches the person whose box
  // you actually type in. (Pre-filling both used to re-save the untouched
  // person too, moving their "since" cutoff forward and dropping transactions —
  // which quietly inflated their remaining balance.)
  const initial = data.household.members.map((m) => ({ memberId: m.id, name: m.name, amount: "" }));
  const [rows, setRows] = useState(initial);
  // A balance you enter is true "right now", so default the date to today.
  const [asOf, setAsOf] = useState(todayISO());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Resulting combined balance = each filled box, falling back to the member's
  // existing saved balance for any box left blank.
  const combined = rows.reduce((t, r) => {
    if (r.amount.trim() !== "") return t + (parseMoney(r.amount) ?? 0);
    const acc = data.funds.accounts.find((a) => a.memberId === r.memberId);
    return t + (acc?.amountMinor ?? 0);
  }, 0);

  function update(id: number, value: string) {
    setRows((rs) => rs.map((r) => (r.memberId === id ? { ...r, amount: value } : r)));
  }

  async function save() {
    // Only send the people whose box was filled in. A blank box leaves that
    // person's balance exactly as it was — so you can update one at a time.
    const accounts: AccountInput[] = [];
    for (const r of rows) {
      if (r.amount.trim() === "") continue;
      const minor = parseMoney(r.amount);
      if (minor == null) return setError(`Enter a valid amount for ${r.name}`);
      accounts.push({ memberId: r.memberId, amountMinor: minor });
    }
    if (accounts.length === 0) return setError("Enter at least one balance to update");
    setSaving(true);
    try {
      await api.setBalance(asOf, accounts);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title="Current balance" onClose={onClose}>
      <p className="modal__lede">
        Enter what’s actually in your account right now. From the moment you save this, anything you
        record — spends, payments, money in, or bills you tick off — adjusts it. Leave a box blank to
        keep that person’s balance unchanged.
      </p>
      <div className="form">
        <div className="form__row">
          {rows.map((r) => {
            const acc = data.funds.accounts.find((a) => a.memberId === r.memberId);
            return (
              <label className="field" key={r.memberId}>
                <span className="field__label">{r.name}’s account (£)</span>
                <input className="input" inputMode="decimal" value={r.amount} onChange={(e) => update(r.memberId, e.target.value)} placeholder={acc ? "Leave blank = no change" : "Enter balance"} />
                {acc && (
                  <span className="field__hint">Currently {formatMoney(acc.amountMinor)} — type a new figure only if it’s changed</span>
                )}
              </label>
            );
          })}
        </div>
        <label className="field">
          <span className="field__label">As of date</span>
          <input className="input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </label>
        <div className="combined-row">
          <span>Combined balance</span>
          <strong>{formatMoney(combined)}</strong>
        </div>
        <div className="info-box">
          This becomes your new starting point. Spends, payments and money in you record from now on
          adjust it — anything already in your account today is captured in the figure above.
        </div>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save balance"}</button>
        </div>
      </div>
    </Modal>
  );
}
